import { inject, Injectable } from '@angular/core';
import { Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { distinctUntilChanged, EMPTY, filter, map, Observable, of, take } from 'rxjs';
import { WINDOW } from '@app/core/providers/window/window.token';

@Injectable({
  providedIn: 'root',
})
export class PageLoadingService {
  private router: Router = inject(Router);
  private window: Window = inject(WINDOW);

  public isInitialNavigation(): boolean {
    return !this.router.navigated;
  }

  public isRootUrl(): boolean {
    const actualPath = this.window.location.pathname || '';

    return /^\/?$/.test(actualPath);
  }

  public isNavigationPending(): Observable<boolean> {
    return this.router.events.pipe(
      filter((event: Event) => this.isConsideredEvent(event)),
      map((event: Event) => this.isNavigationStart(event)),
      distinctUntilChanged()
    );
  }

  public afterActiveNavigationEnd(goOn?: boolean): Observable<boolean | never> {
    if (this.router.getCurrentNavigation()) {
      return this.getEventNavigationEnd().pipe(
        take(1),
        map(() => true)
      );
    }

    return goOn ? of(true) : EMPTY;
  }

  public getRouteData() {
    let route = this.router.routerState.snapshot.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.data;
  }

  public getEventNavigationStart(): Observable<NavigationStart> {
    return this.router.events.pipe(filter((event: Event): event is NavigationStart => this.isNavigationStart(event)));
  }

  public getEventNavigationEnd(): Observable<NavigationEnd> {
    return this.router.events.pipe(filter((event: Event): event is NavigationEnd => this.isNavigationEnd(event)));
  }

  private isConsideredEvent(event: Event): boolean {
    return this.isNavigationStart(event) || this.isNavigationEnd(event);
  }

  private isNavigationStart(event: Event): boolean {
    return event instanceof NavigationStart;
  }

  private isNavigationEnd(event: Event): boolean {
    return event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError;
  }
}
